/**
 * CMS (Condition Monitoring System) API
 * 3-tier alert structure: Alerts (RED), Errors (ORANGE), Notifications (YELLOW)
 */

import { apiClient } from "./client";
import type {
  AlertHistoryParams,
  AlertHistoryResponse,
  AlertResponse,
  AlertsTierResponse,
  AlertUpdate,
  CMSOverview,
  ErrorsTierResponse,
  InverterCountdownResponse,
  NotificationsTierResponse,
  SystemStatus,
  TemperatureMonitoringResponse,
  ZeroYieldResponse,
} from "./types";

/**
 * Get complete CMS overview with all 3 tiers
 */
export async function getCMSOverview(): Promise<CMSOverview> {
  return apiClient.get<CMSOverview>("/api/v1/cms/overview");
}

/**
 * Get RED tier alerts - immediate action required
 */
export async function getAlerts(): Promise<AlertsTierResponse> {
  return apiClient.get<AlertsTierResponse>("/api/v1/cms/alerts");
}

/**
 * Get ORANGE tier errors - technical faults
 */
export async function getErrors(): Promise<ErrorsTierResponse> {
  return apiClient.get<ErrorsTierResponse>("/api/v1/cms/errors");
}

/**
 * Get YELLOW tier notifications - upcoming maintenance
 */
export async function getNotifications(): Promise<NotificationsTierResponse> {
  return apiClient.get<NotificationsTierResponse>("/api/v1/cms/notifications");
}

/**
 * Get temperature monitoring data
 * Threshold: 50°C for overheating
 */
export async function getTemperatureMonitoring(): Promise<TemperatureMonitoringResponse> {
  return apiClient.get<TemperatureMonitoringResponse>("/api/v1/cms/temperature");
}

/**
 * Get zero-yield detection data
 * Devices with zero production during daylight hours
 */
export async function getZeroYield(): Promise<ZeroYieldResponse> {
  return apiClient.get<ZeroYieldResponse>("/api/v1/cms/zero-yield");
}

/**
 * Get inverter replacement countdown
 * Default replacement date: May 1, 2033
 */
export async function getInverterCountdown(): Promise<InverterCountdownResponse> {
  return apiClient.get<InverterCountdownResponse>("/api/v1/cms/inverter-countdown");
}

/**
 * Get system health status
 */
export async function getSystemStatus(): Promise<SystemStatus> {
  return apiClient.get<SystemStatus>("/api/v1/cms/status");
}

/**
 * Acknowledge or resolve an alert
 */
export async function updateAlert(
  alertId: number,
  update: AlertUpdate
): Promise<AlertResponse> {
  return apiClient.patch<AlertResponse>(`/api/v1/cms/alerts/${alertId}`, update);
}

/**
 * Get latest warning for VPP summary table
 */
export async function getLatestWarning(): Promise<string | null> {
  try {
    const overview = await getCMSOverview();
    if (overview.alerts.length > 0) {
      return overview.alerts[0].message;
    }
    if (overview.errors.length > 0) {
      return overview.errors[0].message;
    }
    if (overview.notifications.length > 0) {
      return overview.notifications[0].message;
    }
    return null;
  } catch {
    return null;
  }
}

// ============================================================================
// ALERT HISTORY (NEW 2026-01-19)
// ============================================================================

/**
 * Get historical alerts with filtering and pagination
 * Supports date range, severity, status, plant, and device filters
 */
export async function getAlertHistory(
  params: AlertHistoryParams = {}
): Promise<AlertHistoryResponse> {
  const searchParams = new URLSearchParams();

  if (params.start_date) searchParams.append("start_date", params.start_date);
  if (params.end_date) searchParams.append("end_date", params.end_date);
  if (params.severity) searchParams.append("severity", params.severity);
  if (params.status) searchParams.append("status", params.status);
  if (params.plant_id) searchParams.append("plant_id", params.plant_id.toString());
  if (params.device_name) searchParams.append("device_name", params.device_name);
  if (params.page) searchParams.append("page", params.page.toString());
  if (params.page_size) searchParams.append("page_size", params.page_size.toString());

  const queryString = searchParams.toString();
  const url = `/api/v1/cms/alerts/history${queryString ? `?${queryString}` : ""}`;

  return apiClient.get<AlertHistoryResponse>(url);
}

/**
 * Export alert history as CSV file
 * Returns a Blob for download
 */
export async function exportAlertHistory(
  params: Omit<AlertHistoryParams, "page" | "page_size"> = {}
): Promise<Blob> {
  const searchParams = new URLSearchParams();

  if (params.start_date) searchParams.append("start_date", params.start_date);
  if (params.end_date) searchParams.append("end_date", params.end_date);
  if (params.severity) searchParams.append("severity", params.severity);
  if (params.status) searchParams.append("status", params.status);
  if (params.plant_id) searchParams.append("plant_id", params.plant_id.toString());
  if (params.device_name) searchParams.append("device_name", params.device_name);

  const queryString = searchParams.toString();
  const url = `/api/v1/cms/alerts/history/export${queryString ? `?${queryString}` : ""}`;

  // Use fetch directly for blob response
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    throw new Error(`Export failed: ${response.statusText}`);
  }

  return response.blob();
}

/**
 * Download alert history CSV file
 * Triggers browser download
 */
export async function downloadAlertHistoryCSV(
  params: Omit<AlertHistoryParams, "page" | "page_size"> = {}
): Promise<void> {
  const blob = await exportAlertHistory(params);

  // Generate filename with date range
  const startDate = params.start_date || "last30d";
  const endDate = params.end_date || "today";
  const filename = `alert_history_${startDate}_${endDate}.csv`;

  // Create download link
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
